import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getIdToken } from '../providers/cognito';
import { apiFetch } from '../providers/api-rest-provider';
import { captureSubject, validatedCaptureContext, type CaptureContext } from '../utils/capture-session';

/** Warm-session offline only: no token, expired token or changed session disables capture. */
export function useCaptureContext() {
  const revoked = useRef<string | null>(null);
  const subscribe = useCallback((notify: () => void) => {
    const timer = window.setInterval(() => notify(), 1000);
    const storage = (event: StorageEvent) => {
      if (!event.key || event.key.includes('CognitoIdentityServiceProvider')) {
        revoked.current = getIdToken();
        notify();
      }
    };
    window.addEventListener('storage', storage);
    return () => { window.clearInterval(timer); window.removeEventListener('storage', storage); };
  }, []);
  const snapshot = useCallback(() => {
    const token = getIdToken();
    return token !== revoked.current && captureSubject(token) ? token : null;
  }, []);
  const token = useSyncExternalStore(subscribe, snapshot, () => null);
  const [saved, setSaved] = useState<{ token: string; context: CaptureContext }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || !navigator.onLine) return;
      try {
        const context = validatedCaptureContext(await apiFetch('attempts/capture-context', {}, token), token);
        if (!cancelled) { setSaved({ token, context }); setError(undefined); }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Capture unavailable');
      }
    };
    void load();
    window.addEventListener('online', load);
    return () => { cancelled = true; window.removeEventListener('online', load); };
  }, [token]);
  return {
    context: saved?.token === token ? saved.context : undefined,
    token,
    isCurrent: () => !!token && snapshot() === token,
    error,
  };
}
